<template>
  <div v-if="$store.state.isAuthenticated">

    <!-- Navigation Header Component -->
    <NavHeader :dealerName="accountData && accountData[0]?.name ? accountData[0].name : dealerName" />

    <!-- Modal backdrop -->
    <div v-if="isModalVisible"
      class="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-md z-50 overflow-y-auto p-4 md:p-6">
      
      <!-- Close button - positioned in backdrop for better mobile UX -->
      <button @click="isModalVisible = false" 
        class="fixed top-4 right-4 md:absolute md:top-6 md:right-6 z-50 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all duration-200">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
          class="w-5 h-5 md:w-6 md:h-6 text-gray-900">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <!-- Modal Container -->
      <div class="w-full max-w-4xl mx-auto my-auto">
        <div class="relative croppa bg-black/60 backdrop-blur-lg border border-white/20 rounded-3xl overflow-hidden shadow-2xl p-4 md:p-6">
          <!-- Remove photo button (inside modal) -->
          <div v-if="isCroppaInitCalled">
            <button @click="openConfirmationModal" 
              class="absolute top-4 right-4 z-20 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-md transition-colors duration-200">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span class="sr-only">Remove photo</span>
            </button>
          </div>

          <div class="croppa-container mx-auto" ref="croppaContainer">
            <div v-if="!isCroppaInitCalled" @click="$refs.croppa.chooseFile()"
              class="absolute z-10 cam-35 h-full w-full inset-0 flex flex-col items-center justify-center cursor-pointer">
              <div class="relative z-10 cw--camera mb-6">
                <div id="dot" class="absolute z-10" style="top: 25%; left: 30%; transform: translate(-50%, -50%);">
                  <div class="absolute z-10 ping"></div>
                </div>
              </div>
              <!-- Placeholder text -->
              <div class="relative z-20 text-white text-center mt-6">
                <p class="text-base md:text-lg font-medium mb-2">Capture or upload an image</p>
                <p class="text-sm md:text-base text-white/80">Tap to add photo</p>
              </div>
            </div>

            <img :src="baseplateImageUrl" alt="Baseplate" class="baseplate" />

            <div class="canvas-container relative">
              <vue-croppa :key="croppaKey" ref="croppa" :initial-image="uploadedImageSrc" :settings="croppaSettings"
                :prevent-white-space="true" @file-choose="handleCroppaInit" :disable-drag-and-drop="false" :width="1400" :height="1400"
                :placeholder="''" :show-remove-button="false" :show-loading="true"></vue-croppa>
            </div>

          </div>

          <!-- Image manipulation controls -->
          <div v-if="isCroppaInitCalled" class="w-full mx-auto flex flex-wrap justify-center gap-2 md:gap-3 mt-4 md:mt-6">
            <button type="button"
              class="py-2.5 px-5 md:py-3 md:px-6 inline-flex justify-center items-center gap-2 rounded-full font-bold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 text-xs md:text-sm shadow-lg transform hover:scale-105"
              @click="$refs.croppa.rotate(-1)">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Rotate</span>
            </button>
            <button
              class="py-2.5 px-5 md:py-3 md:px-6 inline-flex justify-center items-center gap-2 rounded-full font-bold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 text-xs md:text-sm shadow-lg transform hover:scale-105"
              @click="$refs.croppa.flipX()">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
              </svg>
              <span>Flip H</span>
            </button>
            <button
              class="py-2.5 px-5 md:py-3 md:px-6 inline-flex justify-center items-center gap-2 rounded-full font-bold bg-blue-600 text-white hover:bg-blue-700 transition-all duration-200 text-xs md:text-sm shadow-lg transform hover:scale-105"
              @click="$refs.croppa.flipY()">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
              </svg>
              <span>Flip V</span>
            </button>
          </div>

          <!-- Continue button section -->
          <div class="w-full my-4 md:my-6 mx-auto px-2 md:px-4">
            <button type="button" @click="captureImage" v-if="isCroppaInitCalled"
              :disabled="isCapturing"
              class="w-full inline-flex bg-green-600 text-left hover:bg-green-700 px-6 py-3 md:px-8 md:py-4 rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
              :class="{ 'opacity-50 cursor-not-allowed': isCapturing }">
              <span class="flex items-center w-full">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                  stroke="currentColor" class="w-5 h-5 md:w-6 md:h-6 text-white mr-3 md:mr-4 flex-shrink-0">
                  <path stroke-linecap="round" stroke-linejoin="round"
                    d="M15.182 15.182a4.5 4.5 0 01-6.364 0M21 12a9 9 0 11-18 0 9 9 0 0118 0zM9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75zm-.375 0h.008v.015h-.008V9.75zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75zm-.375 0h.008v.015h-.008V9.75z" />
                </svg>
                <span class="grow">
                  <span class="block text-base md:text-lg font-bold text-white">
                    Continue to customer details
                  </span>
                </span>
              </span>
            </button>
            <p class="text-center text-xs md:text-sm text-white/70 mt-2 md:mt-3">
              Provide essential information about the customer.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="relative flex flex-col items-center justify-center min-h-screen z-10 pt-4 md:pt-8">
      <div class="w-full md:max-w-sm max-w-sm mx-auto overflow-y-auto">
        <!-- <WelcomeForm :capturedImageBlob="capturedImageBlob" @open-modal="openModal" /> -->
        <WelcomeForm v-if="accountData[0]?.form" :capturedImageBlob="capturedImageBlob" :originalImageBlob="originalImageBlob" :isBlurring="isBlurring" @open-modal="openModal"
          @form-success="onFormSuccess" @update-form-title="handleTitleFromChild" :formID="accountData[0]?.form" />
      </div>
    </div>

    <!-- Add the confirmation modal -->
    <ConfirmationModal v-if="showConfirmationModal" @confirm="handleConfirmAction" @cancel="closeConfirmationModal"
      message="Are you sure you want to remove photo?" />
    <div class="fixed inset-0 bg-gray-900 -z-2"></div>

  </div>
  <div v-else>
    <login :post="accountData[0]?.id" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState, mapActions } from 'vuex';
import Croppa from 'vue-croppa';

Vue.use(Croppa, { componentName: 'vue-croppa' });
import 'vue-croppa/dist/vue-croppa.css';

import WelcomeForm from '@/components/form.vue'
import ConfirmationModal from '@/components/ConfirmationModal.vue';
import login from '@/components/login.vue'
import NavHeader from '@/components/NavHeader.vue';
export default {
  name: 'YourComponent',
  // beforeRouteEnter(to, from, next) {
  //   console.log('Entering navigation guard');
  //   next(vm => {
  //     const slug = to.params.slug;
  //     console.log('Slug:', slug);
  //     vm.fetchAccountData(slug);
  //   });
  // },
  data() {
    return {
      isCapturing: false,
      isBlurring: false,
      blurAbortController: null,
      width: 300,
      height: 300,
      uploadedImageSrc: null,
      croppaKey: 0, // Key to force croppa re-render
      croppaSettings: {
        aspectRatio: 1,
        viewMode: 2,
      },
      croppaWidth: 900,
      croppaHeight: 900,
      capturedImageBlob: null,
      capturedImageSrc: null,
      originalImageBlob: null, // Original image before blur, for R2 storage
      baseplateImageUrl: null,
      isCroppaInitCalled: false,
      activeTab: 'tabs-with-card-1',
      windowResizeListenerAdded: false,
      isModalVisible: false,
      showConfirmationModal: false,
      formTitle: '', // Initialize a local formTitle property
      logoURLWH: '/assets/engagr-wh.svg'
    };
  },        

  components: {
    WelcomeForm,
    ConfirmationModal,
    login,
    NavHeader
  },
  computed: {
    ...mapState(['accountData']),
    dealerName() {
      // Get dealer name from slug - convert slug format to display format
      const slug = this.$route.params.slug;
      if (slug) {
        // Convert slug like "courtney-patterson-ford" to "Courtney Patterson Ford"
        return slug.split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
      }
      return 'Dealership';
    },
    computedBaseplateImageUrl() {
      if (this.accountData && this.accountData.length > 0) {
        return this.accountData[0].baseplate;
      } else {
        return null; // Return null or a default image URL if accountData is not available yet
      }
    }
  },
  watch: {
    // Watch for changes in accountData and update baseplateImageUrl accordingly
    accountData: {
      handler() {
        this.baseplateImageUrl = this.computedBaseplateImageUrl;
      },
      immediate: true, // Trigger the handler immediately
    },
  },
  methods: {
    ...mapActions(['fetchAccountData']),

    openConfirmationModal() {
      this.showConfirmationModal = true;
    },

    closeConfirmationModal() {
      this.showConfirmationModal = false;
    },

    handleConfirmAction() {
      // Abort any in-flight background blur
      if (this.blurAbortController) {
        this.blurAbortController.abort();
        this.blurAbortController = null;
      }
      // Perform actions you want to execute when confirmed
      this.$refs.croppa.refresh();
      this.closeCroppaInitCalled();
      this.originalImageBlob = null;
      if (this.uploadedImageSrc && this.uploadedImageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(this.uploadedImageSrc);
      }
      this.uploadedImageSrc = null;

      // Close the confirmation modal
      this.closeConfirmationModal();
    },

    onFormSuccess() {
      // Handle form submission success here
      console.log('Form submitted successfully');

      // Abort any in-flight background blur
      if (this.blurAbortController) {
        this.blurAbortController.abort();
        this.blurAbortController = null;
      }
      // Clear / Reset the captured image blob by setting it to null
       this.capturedImageBlob = null;
       this.originalImageBlob = null;
       if (this.uploadedImageSrc && this.uploadedImageSrc.startsWith('blob:')) {
         URL.revokeObjectURL(this.uploadedImageSrc);
       }
       this.uploadedImageSrc = null;
       if (this.$refs.croppa) {
         this.$refs.croppa.refresh();
       }
      this.closeCroppaInitCalled();

      // Additional actions after form success can be added here

      // For example, showing a success message
      this.modalTitle = 'Success';
      this.modalMessage = 'Form submitted successfully.';
      this.showModal = true;
    },
    openModal() {
      this.isModalVisible = true;

      // this.$nextTick(() => {
      //   this.updateCroppaDimensions();
      // });
    },
    closeModal() {
      this.isModalVisible = false;
    },
    confirmAction() {
      // Add your action here (e.g., submit a form) and then close the modal
      this.closeModal();
    },
    async handleCroppaInit() {
      this.isCroppaInitCalled = true;

      // Wait for croppa to process the file, then save original for R2
      await this.$nextTick();
      setTimeout(async () => {
        if (!this.$refs.croppa) return;
        try {
          const canvas = this.$refs.croppa.getCanvas();
          if (!canvas) return;

          // Save the original (pre-blur, no baseplate) as JPEG for R2 storage
          const blob = await new Promise((resolve) => {
            canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.85);
          });
          if (blob) {
            this.originalImageBlob = blob;
            // Save blob URL so croppa can reload when modal reopens (v-if destroys it)
            if (this.uploadedImageSrc && this.uploadedImageSrc.startsWith('blob:')) {
              URL.revokeObjectURL(this.uploadedImageSrc);
            }
            this.uploadedImageSrc = URL.createObjectURL(blob);
          }
        } catch (error) {
          console.error('Error saving original image:', error);
        }
      }, 500);
    },
    closeCroppaInitCalled() {
      this.isCroppaInitCalled = false;
    },
    async captureImage() {
      if (this.isCapturing) return; // Prevent double-click
      this.isCapturing = true;

      try {
        // Get raw photo canvas from croppa (1400x1400, no baseplate)
        const photoCanvas = this.$refs.croppa.getCanvas();
        if (!photoCanvas) return;

        // Composite photo + crisp baseplate for instant preview
        const compositeBlob = await this.compositeWithBaseplate(photoCanvas);

        // Set composite as capturedImageBlob
        if (this.capturedImageSrc && this.capturedImageSrc.startsWith('blob:')) {
          URL.revokeObjectURL(this.capturedImageSrc);
        }
        this.capturedImageBlob = compositeBlob;
        this.capturedImageSrc = URL.createObjectURL(compositeBlob);

        // Close modal instantly — user starts filling in the form
        this.confirmAction();

        // Get raw photo blob (no baseplate) for background blur
        const photoBlob = await new Promise((resolve) => {
          photoCanvas.toBlob((b) => resolve(b), 'image/png');
        });

        // Blur only the photo in background (baseplate composited after blur completes)
        this.blurInBackground(photoBlob);
      } finally {
        this.isCapturing = false;
      }
    },

    async blurInBackground(blob) {
      // Abort any previous background blur
      if (this.blurAbortController) {
        this.blurAbortController.abort();
      }

      this.isBlurring = true;
      try {
        const compressedBlob = await this.compressImage(blob);
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result.split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(compressedBlob);
        });

        const controller = new AbortController();
        this.blurAbortController = controller;
        const timeout = setTimeout(() => controller.abort(), 20000);

        const response = await fetch('/.netlify/functions/blur-plate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageBase64: base64 }),
          signal: controller.signal,
        });

        clearTimeout(timeout);
        const data = await response.json();

        if (data.success && data.processedImageBase64 && this.capturedImageBlob) {
          // Only swap if the user hasn't cleared the image while we were blurring
          const byteString = atob(data.processedImageBase64);
          const byteArray = new Uint8Array(byteString.length);
          for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
          }
          const blurredPhotoBlob = new Blob([byteArray], { type: 'image/jpeg' });

          // Composite blurred photo + crisp baseplate (baseplate never touches blur pipeline)
          const blurredImg = await this.loadImage(blurredPhotoBlob);
          const compositeBlob = await this.compositeWithBaseplate(blurredImg);

          if (this.capturedImageSrc && this.capturedImageSrc.startsWith('blob:')) {
            URL.revokeObjectURL(this.capturedImageSrc);
          }
          this.capturedImageBlob = compositeBlob;
          this.capturedImageSrc = URL.createObjectURL(compositeBlob);
          console.log(`Background blur complete: ${data.platesDetected} plate(s) blurred`);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.warn('Background blur failed, using original capture:', err);
      } finally {
        this.blurAbortController = null;
        this.isBlurring = false;
      }
    },



    loadImage(source) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        if (source instanceof Blob) {
          const url = URL.createObjectURL(source);
          img.onload = () => {
            URL.revokeObjectURL(url);
            resolve(img);
          };
          img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image from blob'));
          };
          img.src = url;
        } else {
          img.onload = () => resolve(img);
          img.onerror = () => reject(new Error('Failed to load image'));
          img.src = source;
        }
      });
    },

    async compositeWithBaseplate(photoSource) {
      const targetSize = 1400;
      const canvas = document.createElement('canvas');
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');

      // Draw photo (canvas element or Image element)
      ctx.drawImage(photoSource, 0, 0, targetSize, targetSize);

      // Overlay crisp baseplate at the bottom (same positioning as CSS: bottom: 0, width: 100%)
      if (this.baseplateImageUrl) {
        try {
          const baseplateImg = await this.loadImage(this.baseplateImageUrl);
          const bpHeight = (targetSize / baseplateImg.naturalWidth) * baseplateImg.naturalHeight;
          ctx.drawImage(baseplateImg, 0, targetSize - bpHeight, targetSize, bpHeight);
        } catch (err) {
          console.warn('Failed to load baseplate for composite, skipping:', err);
        }
      }

      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Failed to create composite image'));
        }, 'image/png');
      });
    },

    dataURLtoBlob(dataURL) {
      const contentType = 'image/png'; // Explicitly set MIME type to 'image/png'
      const parts = dataURL.split(',');
      const byteString = atob(parts[1]);
      const byteStringLength = byteString.length;
      const byteArray = new Uint8Array(byteStringLength);

      for (let i = 0; i < byteStringLength; i++) {
        byteArray[i] = byteString.charCodeAt(i);
      }

      return new Blob([byteArray], { type: contentType });
    },

    async compressImage(blob) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        const blobUrl = URL.createObjectURL(blob);
        img.onload = () => {
          // Calculate new dimensions (max 1920px width/height, maintain aspect ratio)
          const maxDimension = 1920;
          let width = img.width;
          let height = img.height;

          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = (height / width) * maxDimension;
              width = maxDimension;
            } else {
              width = (width / height) * maxDimension;
              height = maxDimension;
            }
          }

          // Create canvas and compress
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          // Draw resized image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression (0.85 quality for JPEG-like compression)
          canvas.toBlob((compressedBlob) => {
            URL.revokeObjectURL(blobUrl);
            if (compressedBlob) {
              resolve(compressedBlob);
            } else {
              reject(new Error('Failed to compress image'));
            }
          }, 'image/jpeg', 0.85); // Use JPEG with 85% quality for better compression
        };

        img.onerror = () => {
          URL.revokeObjectURL(blobUrl);
          reject(new Error('Failed to load image for compression'));
        };
        img.src = blobUrl;
      });
    },

    handleFileUpload(event) {
      this.uploadedImageSrc = URL.createObjectURL(event.target.files[0]);
    },
    handleTitleFromChild(data) {
      this.formTitle = data;
    },
  },
  created() {
    //console.log(this.$router.currentRoute.params);
    this.fetchAccountData(this.$router.currentRoute.params.slug);
  },
  beforeDestroy() {
    // Abort any in-flight background blur
    if (this.blurAbortController) {
      this.blurAbortController.abort();
      this.blurAbortController = null;
    }
    // Revoke blob URLs to prevent memory leaks
    if (this.uploadedImageSrc && this.uploadedImageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(this.uploadedImageSrc);
    }
    if (this.capturedImageSrc && this.capturedImageSrc.startsWith('blob:')) {
      URL.revokeObjectURL(this.capturedImageSrc);
    }
  },
};
</script>




<style>
.fixed {
  position: fixed;
}

/* Modern header styles */
.bg-gray-900 {
  background-color: #1a202c;
}

/* Modal improvements for mobile */
@media (max-width: 768px) {
  .croppa {
    max-height: calc(100vh - 2rem);
    overflow-y: auto;
  }
}

/* Improved croppa container */
.croppa-container {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

/* Button hover effects */
button {
  transition: all 0.2s ease-in-out;
}

button:hover {
  transform: translateY(-1px);
}

button:active {
  transform: translateY(1px);
}

.canvas-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.croppa-container canvas {
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
}


.cw--camera {
  width: 70px;
  height: 100px;
  background: url('https://s3-ap-southeast-2.amazonaws.com/adus/cw--camera.svg') no-repeat top left;
  background-size: contain;
}

@media (max-width: 768px) {
  .cw--camera {
    width: 60px;
    height: 85px;
  }
}

.baseplate-container {
  width: 100%;
  max-width: 900px;
  /* Set a maximum width if needed */
  overflow: hidden;
}

.baseplate {
  width: 100%;
  height: auto;
  position: absolute;
  bottom: 0;
  z-index: 2;
}

.cam-35 {
  padding-bottom: 20%;
}

.croppa-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 900px;
  background-color: #212328;
  min-height: 300px;
}

@media (max-width: 768px) {
  .croppa-container {
    min-height: 250px;
  }
}

.croppa-container img {
  width: 100%;
  height: auto;
}

.croppa-container:hover {
  opacity: 1;
}

.icon.icon-remove {
  top: -14px !important;
    left: -14px !important;
    width: 60px;
    height: 60px;
    margin: 10px;
    position: absolute;
    border-radius: 50%;
    filter: none !important;
    z-index: 10;
    cursor: pointer;
    border: none;
}

@media (max-width: 1200px) {
  .icon.icon-remove {
    top: -8px !important;
    left: -8px !important;
    right: auto !important;
    width: 40px;
    height: 40px;
  }
}


@-webkit-keyframes sonar {
  0% {
    background-color: rgba(255, 255, 255, 1);
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }

  50% {
    -webkit-transform: scale(2);
    transform: scale(2);
    opacity: 0.2;
  }

  100% {
    -webkit-transform: scale(6);
    transform: scale(6);
    opacity: 0;
  }
}

@keyframes sonar {
  0% {
    background-color: rgba(255, 255, 255, 1);
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }

  50% {
    -webkit-transform: scale(2);
    transform: scale(2);
    opacity: 0.2;
  }

  100% {
    -webkit-transform: scale(6);
    transform: scale(6);
    opacity: 0;
  }
}

/*Pulse*/

.pulse-ring {
  position: relative;
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
}

.pulse-ring:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.6);
  animation: pulse 2s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7);
  }
  70% {
    transform: scale(1.2);
    box-shadow: 0 0 0 15px rgba(255, 255, 255, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
  }
}

/* Phone Landscape Warning Web Clip*/

/* Ping animation */
.ping {
  display: inline-flex;
  position: relative;
  width: 25px;
  height: 25px;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 50%;
}

.ping::before {
  content: '';
  position: absolute;
  top: -150%;
  left: -150%;
  width: 400%;
  height: 400%;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.8);
  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}

@keyframes ping {
  75%, 100% {
    transform: scale(4.8);
    opacity: 0;
  }
}

</style>
